/**
 * PATH: packages/document-editor/src/pagination.ts
 * FILE_ID: DOCEDITOR-PAGINATION-MODEL-001
 * PURPOSE: Deterministic pagination model for explicit breaks and measured blocks.
 * WHY: Browser pagination stays testable while one ProseMirror owns editing state.
 */

export type PaginationBreak = 'none' | 'page' | 'section-next-page'

export interface MeasuredDocumentBlock {
  heightPx: number
  break: PaginationBreak
}

export interface BlockPagePlacement {
  page: number
  spacerBeforePx: number
}

export interface PaginationLayout {
  pageCount: number
  placements: BlockPagePlacement[]
}

export interface PaginationMetrics {
  contentHeightPx: number
  pageTransitionPx: number
}

const finiteNonNegative = (value: number) =>
  Number.isFinite(value) ? Math.max(0, value) : 0

export function calculatePaginationLayout(
  blocks: readonly MeasuredDocumentBlock[],
  metrics: PaginationMetrics,
): PaginationLayout {
  const contentHeight = Math.max(1, finiteNonNegative(metrics.contentHeightPx))
  const transition = finiteNonNegative(metrics.pageTransitionPx)
  let page = 1
  let used = 0
  const placements: BlockPagePlacement[] = []

  for (const block of blocks) {
    const height = finiteNonNegative(block.heightPx)
    if (block.break !== 'none') {
      const spacerBeforePx = Math.max(0, contentHeight - used) + transition
      page += 1
      used = 0
      placements.push({ page, spacerBeforePx })
      continue
    }

    let spacerBeforePx = 0
    if (used > 0 && used + height > contentHeight) {
      spacerBeforePx = Math.max(0, contentHeight - used) + transition
      page += 1
      used = 0
    }
    placements.push({ page, spacerBeforePx })

    const occupiedPages = Math.max(1, Math.ceil(height / contentHeight))
    page += occupiedPages - 1
    used = height % contentHeight
    if (height > 0 && used === 0) used = contentHeight
  }

  return { pageCount: Math.max(1, page), placements }
}
